import clsx from 'clsx';
import type { MouseEvent, ReactElement, Ref } from 'react';
import { Children, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { getElementByTagNameRecursive } from '../common-functions';
import TabViewHeader from './tab-view-header.component';
import type { TabViewPanelProps } from './tab-view-panel.component';
import TabViewPanel from './tab-view-panel.component';
import type { ElementNavigatorRef } from './element-navigator.component';
import ElementNavigator from './element-navigator.component';

interface TabViewStyles {
  containerStyles?: string;
  tabStyles?: string;
  activeTabStyles?: string;
  inactiveTabStyles?: string;
  navigationContainerStyles?: string;
  navigationStyles?: string;
  scrollerBorderStyles?: string;
  scrollerStyles?: string;
  panelStyles?: string;
}

export interface TabViewProps {
  activeIndex?: number;
  onTabChange?: (index: number) => void;
  showScrollbars?: boolean;
  children: ReactElement<TabViewPanelProps> | Array<ReactElement<TabViewPanelProps>>;
  styles?: TabViewStyles;
  id?: string;
}

export interface TabViewRef {
  setActiveTab: (index: number) => void;
  setScrollTab: (index: number) => void;
}

function TabView(props: TabViewProps, ref: Ref<TabViewRef>) {
  const { activeIndex = 0, onTabChange, showScrollbars = true, children, styles = {}, id } = props;

  const {
    containerStyles,
    tabStyles,
    activeTabStyles,
    inactiveTabStyles,
    navigationContainerStyles,
    navigationStyles,
    scrollerBorderStyles,
    scrollerStyles,
    panelStyles,
  } = {
    containerStyles: 'odin-flex odin-h-full odin-flex-col',
    tabStyles: 'odin-p-1 odin-mr-4 odin-cursor-pointer hover:odin-text-primary-1',
    activeTabStyles: 'odin-border-b-4 odin-border-solid odin-border-primary-1 odin-text-primary-1 odin-font-bold',
    inactiveTabStyles: 'odin-border-b odin-border-solid odin-border-primary-1',
    navigationStyles:
      'odin-list-none odin-bg-mono-1 odin-grow odin-flex odin-flex-row odin-overflow-x-scroll no-scrollbar fix-ul',
    panelStyles: '',
    ...styles,
  };

  const [activeTabIndex, setActiveTabIndex] = useState(activeIndex || 0);

  const tabNavigationRef = useRef<HTMLUListElement>();
  const carouselRef = useRef<ElementNavigatorRef>(null);

  useEffect(() => {
    if (activeIndex !== activeTabIndex) {
      setActiveTabIndex(activeIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only sync when activeIndex prop changes
  }, [activeIndex]);

  const onTabClicked = (event: MouseEvent, tabIndex: number) => {
    setActiveTabIndex(tabIndex);

    if (onTabChange) {
      onTabChange(tabIndex);
    }

    const tabElement = getElementByTagNameRecursive(event.target as Element, 'li');
    if (tabElement && tabNavigationRef.current) {
      tabElement.scrollIntoView(false);
    }
  };

  const setActiveTab = (index: number) => {
    setActiveTabIndex(index);
  };

  const setScrollTab = (index: number) => {
    carouselRef.current?.setScrollTab(index);
  };

  useImperativeHandle(ref, () => ({
    setActiveTab,
    setScrollTab,
  }));

  const renderTabNavigator = () => {
    const headers = Children.map(children, (tab, index) => {
      return (
        <TabViewHeader
          tab={tab}
          activeIndex={activeTabIndex}
          currentIndex={index}
          onClick={onTabClicked}
          styles={{ tabStyles, activeTabStyles, inactiveTabStyles }}
        />
      );
    });

    const navigationFinalStyles = clsx(navigationStyles, scrollerBorderStyles);

    return (
      <ElementNavigator
        ref={carouselRef}
        id={id}
        activeIndex={activeTabIndex}
        elementNavigatorStyles={navigationFinalStyles}
        elementNavigatorContainerStyles={navigationContainerStyles}
        scrollerStyles={scrollerStyles}
      >
        {headers}
      </ElementNavigator>
    );
  };

  const renderContent = () => {
    return Children.map(children, (tab, index) => {
      return (
        <TabViewPanel
          {...tab.props}
          active={activeTabIndex === index}
          panelStyles={panelStyles}
          showScrollbars={showScrollbars}
        />
      );
    });
  };

  return (
    <div className={containerStyles}>
      {renderTabNavigator()}
      {renderContent()}
    </div>
  );
}

export default forwardRef(TabView);
